import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import CheckBox from '../CheckBox';
import styles from './styles.module.css';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';

type Variant = 'contain' | 'error';
export type ValueObj = {
  name?: string;
  label: string;
  value: string;
  checked: boolean;
};
export type CheckBoxGroupOnChangeProps = {
  values: ValueObj[];
  selectedValues: string[];
};
type StyleType = 'default' | 'carCategory';
type Props = {
  values: ValueObj[];
  inputs?: (
    values: ValueObj[],
    onSelect: (selected: ValueObj) => void
  ) => ReactElement;
  onChange: (newValues: CheckBoxGroupOnChangeProps) => void;
  placeholder?: string;
  variants?: Variant[];
  isVertical?: boolean;
  styleType?: StyleType;
};

const CheckBoxGroup: FC<Props> = ({
  values,
  inputs,
  onChange,
  placeholder = '',
  variants = [],
  isVertical = false,
  styleType = 'default',
}) => {
  const [popupVisible, updatePopupVisible] = useState(false);
  const open = useCallback(
    () => updatePopupVisible(true),
    [updatePopupVisible]
  );
  const close = useCallback(
    () => updatePopupVisible(false),
    [updatePopupVisible]
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!popupVisible) return;
      // 自身の子孫のクリックでは閉じないようにする
      if (!(event.target instanceof Node)) return;
      const container = containerRef.current;
      if (container && container.contains(event.target)) return;
      // その他のクリックでは閉じる
      close();
    };
    window.addEventListener('click', handler, { capture: true });
    return () => {
      window.removeEventListener('click', handler, { capture: true });
    };
  });

  const selectAll = () => {
    const newChecked = !values.every((v) => v.checked);
    const newValues = values.map((v) => ({ ...v, checked: newChecked }));
    const selectedValues = newValues
      .filter((v) => v.checked)
      .map((v) => v.value);
    onChange({ values: newValues, selectedValues });
  };
  const onSelect = (selected: ValueObj) => {
    const newValues = values.map((v) => {
      if (v.value !== selected.value) {
        return v;
      }
      return {
        ...v,
        checked: !selected.checked,
      };
    });
    const selectedValues = newValues
      .filter((v) => v.checked)
      .map((v) => v.value);
    onChange({ values: newValues, selectedValues });
  };

  const leftLine = values.filter((_, i, arr) => i < arr.length / 2);
  const rightLine = values.filter((_, i, arr) => i >= arr.length / 2);
  const lineDOM = (v: ValueObj) => (
    <div className={styles.item} key={v.value}>
      <CheckBox
        value={v.value}
        checked={v.checked}
        onChange={() => onSelect(v)}
        name={v.name}
        label={v.label}
      />
    </div>
  );
  const selected = values.filter((v) => v.checked);
  const ValuePopUp = inputs ? (
    inputs(values, onSelect)
  ) : (
    <div
      ref={containerRef}
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={classNames(
        styles.popupContainer,
        isVertical && styles.popupVertical,
        styleType === 'carCategory' && styles.popupCarCategory
      )}
    >
      <div className={styles.item}>
        <CheckBox
          checked={values.every((v) => v.checked)}
          onChange={selectAll}
          label="すべてを選択"
        />
      </div>
      <div className={styles.popup}>
        {isVertical ? (
          <div className={styles.line}>{values.map(lineDOM)}</div>
        ) : (
          <>
            <div
              className={
                styleType === 'carCategory'
                  ? styles.lineCarCategoryLeft
                  : styles.line
              }
            >
              {leftLine.map(lineDOM)}
            </div>
            <div className={styles.line}>{rightLine.map(lineDOM)}</div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={classNames(styles.opener, ...variants.map((v) => styles[v]))}
      onClick={(e) => {
        e.stopPropagation();
        popupVisible ? close() : open();
      }}
      tabIndex={0}
    >
      <FontAwesomeIcon icon={faAngleDown} className={styles.icon} />
      {selected.length ? (
        <div className={styles.selectedText}>
          {selected.map((v) => v.label).join(', ')}
        </div>
      ) : (
        <span className={styles.placeholder}>{placeholder}</span>
      )}
      {popupVisible && ValuePopUp}
    </div>
  );
};

export default CheckBoxGroup;
